import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useCustomer } from '../context/CustomerContext';
import { MEDUSA_API_URL, MEDUSA_API_KEY } from 'services/medusa/config';
import { attachDistributorPrices } from 'services/medusa/distributor';
import { sortVariantsByPrice } from 'services/medusa/sortVariants';
import ProductCarousel from '../components/ProductCarousel';
import ProductReviews from '../components/ProductReviews';
import { getVariantSpec } from '../utils/variantSpec';
import LoadingDots from '../components/shop/LoadingDots';
import SEO, { productJsonLd, breadcrumbJsonLd } from '../components/SEO';
import { lockScroll, unlockScroll } from '../utils/scrollLock';
import '../styles/ProductDetailPhys.css';
import { sitePathsIn } from 'utils/sitePath';
import { formatAmount } from '../utils/money';
import { isVariantInStock, isProductInStock } from '../utils/stock';
import { usePromo2x1, textoOferta, etiquetaOferta, precioConDescuento, buyGetDe, descuentoDe } from '../hooks/usePromo2x1';

// Sin fallback hardcodeado. Requiere REACT_APP_MEDUSA_REGION_ID en .env;
// si falta, las llamadas a /store/products fallarán de forma visible en vez de
// devolver precios resueltos contra una región equivocada.
const REGION_ID = process.env.REACT_APP_MEDUSA_REGION_ID;
if (!REGION_ID && process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.warn('[ProductDetail] REACT_APP_MEDUSA_REGION_ID no está definido en .env — precios y regiones no se resolverán correctamente.');
}

// Loading dots para botón
function ButtonLoadingDots() {
  return (
    <span className="button-loading-dots">
      <span className="dot"></span>
      <span className="dot"></span>
      <span className="dot"></span>
      <style>{`
        .button-loading-dots {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          height: 20px;
        }
        .button-loading-dots .dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #fff;
          animation: dotPulse 0.6s ease-in-out infinite;
        }
        .button-loading-dots .dot:nth-child(1) { animation-delay: 0ms; }
        .button-loading-dots .dot:nth-child(2) { animation-delay: 150ms; }
        .button-loading-dots .dot:nth-child(3) { animation-delay: 300ms; }
        @keyframes dotPulse {
          0%, 100% { opacity: 0.4; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </span>
  );
}

// Helper para convertir URLs de YouTube/Vimeo a embed
function getEmbedUrl(url) {
  if (!url) return null;
  
  // YouTube
  const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1&mute=1&loop=1&playlist=${youtubeMatch[1]}`;
  }
  
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&muted=1&loop=1`;
  }
  
  // Direct video URL (MP4, etc)
  if (url.match(/\.(mp4|webm|ogg)$/i)) {
    return url;
  }
  
  return url;
}

function VideoPlayer({ url, className }) {
  const embedUrl = getEmbedUrl(url);
  
  if (!embedUrl) return null;
  
  // Direct video file
  if (url.match(/\.(mp4|webm|ogg)$/i)) {
    return (
      <video 
        className={className}
        autoPlay 
        muted 
        loop 
        playsInline
      >
        <source src={embedUrl} type="video/mp4" />
      </video>
    );
  }
  
  // YouTube/Vimeo embed
  return (
    <iframe
      className={className}
      src={embedUrl}
      frameBorder="0"
      allow="autoplay; encrypted-media"
      allowFullScreen
      title="Product video"
    />
  );
}

export default function ProductDetailPhys() {
  const promo2x1 = usePromo2x1();
  const { productId } = useParams();
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { isDistributor } = useCustomer();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showIngredientsPanel, setShowIngredientsPanel] = useState(false);
  const [buttonState, setButtonState] = useState('idle');
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Hover sobre el overlay fullscreen → activa las flechas (slide-in).
  const [fullscreenHover, setFullscreenHover] = useState(false);
  // Seguidor "+" que persigue al cursor sobre la foto (abre fullscreen al click)
  const [zoomCursor, setZoomCursor] = useState({ x: 0, y: 0, visible: false });

  // Block body scroll when ingredients panel is open
  useEffect(() => {
    if (showIngredientsPanel) {
      lockScroll();
    } else {
      unlockScroll();
    }
    return () => { unlockScroll(); };
  }, [showIngredientsPanel]);

  // Fullscreen gallery: lock scroll + keyboard navigation (ESC / arrows).
  // Usa product?.images?.length para evitar TDZ con `images` (declarada más abajo).
  useEffect(() => {
    if (!isFullscreen) return;
    const total = product?.images?.length || 1;
    lockScroll();
    const onKey = (e) => {
      if (e.key === 'Escape') setIsFullscreen(false);
      if (e.key === 'ArrowRight') setCurrentImageIndex((prev) => (prev + 1) % total);
      if (e.key === 'ArrowLeft') setCurrentImageIndex((prev) => (prev - 1 + total) % total);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      unlockScroll();
      window.removeEventListener('keydown', onKey);
    };
  }, [isFullscreen, product?.images?.length]);

  // Sinergia products
  const [synergiaProducts, setSynergiaProducts] = useState([]);
  const [expandedSynergia, setExpandedSynergia] = useState(null);
  
  // Más detalles accordion
  const [expandedDetail, setExpandedDetail] = useState(null);
  
  // Otras recomendaciones
  const [recommendedProducts, setRecommendedProducts] = useState([]);

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // El path param puede ser un handle SEO-friendly (vitamin-c-face-mist)
      // o un ID legacy (prod_01KKJ...). Detectamos con el prefijo `prod_`.
      // Para handle usamos /store/products?handle=X (Medusa devuelve {products:[]}).
      // Para ID usamos /store/products/X (devuelve {product}).
      const isLegacyId = /^prod_/.test(productId);
      const inventoryFields = '+variants.manage_inventory,+variants.allow_backorder,+variants.inventory_quantity';
      const url = isLegacyId
        ? `${MEDUSA_API_URL}/store/products/${productId}?region_id=${REGION_ID}&fields=+metadata,+thumbnail,*images,*variants.calculated_price,${inventoryFields}`
        : `${MEDUSA_API_URL}/store/products?handle=${encodeURIComponent(productId)}&region_id=${REGION_ID}&fields=+metadata,+thumbnail,*images,*variants.calculated_price,${inventoryFields}&limit=1`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-publishable-api-key': MEDUSA_API_KEY,
        },
      });

      if (!response.ok) {
        throw new Error('Producto no encontrado');
      }

      const data = await response.json();
      const productObj = isLegacyId ? data.product : data.products?.[0];
      if (!productObj) {
        throw new Error('Producto no encontrado');
      }
      await attachDistributorPrices([productObj]);
      sortVariantsByPrice(productObj);
      setProduct(productObj);

      // Mantengo el alias `data.product` abajo para no romper los reads que siguen.
      data.product = productObj;

      if (data.product.variants && data.product.variants.length > 0) {
        setSelectedVariant(data.product.variants[0]);
      }

      // Fetch sinergia products
      const synergiaIds = data.product.metadata?.sinergia_ids || [];
      
      if (synergiaIds && synergiaIds.length > 0) {
        fetchRelatedProducts(synergiaIds, setSynergiaProducts);
      }

      // Fetch recommended products
      const recommendedIds = data.product.metadata?.recommended_ids || [];
      
      if (recommendedIds && recommendedIds.length > 0) {
        fetchRelatedProducts(recommendedIds, setRecommendedProducts);
      }
    } catch (err) {
      console.error('Error fetching product:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProduct();
  }, [productId, fetchProduct]);

  const fetchRelatedProducts = async (ids, setter) => {
    try {
      const products = await Promise.all(
        ids.map(async (id) => {
          const response = await fetch(
            `${MEDUSA_API_URL}/store/products/${id}?region_id=${REGION_ID}&fields=+thumbnail,*images,*variants.calculated_price,+variants.manage_inventory,+variants.allow_backorder,+variants.inventory_quantity`,
            {
              headers: {
                'Content-Type': 'application/json',
                'x-publishable-api-key': MEDUSA_API_KEY,
              },
            }
          );
          if (response.ok) {
            const data = await response.json();
            return data.product ? sortVariantsByPrice(data.product) : null;
          }
          return null;
        })
      );
      setter(products.filter(Boolean));
    } catch (err) {
      console.error('Error fetching related products:', err);
    }
  };

  // Get images for current variant
  const getVariantImages = () => {
    if (!product) return [];

    // Sets/Kits: la foto #1 es la vertical reservada para el grid. En el single
    // product mostramos las horizontales a partir de la #2.
    const productIsKit = ['kit', 'set'].includes((product.type?.value || '').trim().toLowerCase());
    const dropGridCover = (list) =>
      productIsKit && list.length > 1 ? list.slice(1) : list;

    if (selectedVariant?.thumbnail) {
      const variantImage = selectedVariant.thumbnail;
      const otherImages = product.images?.map(img => img.url).filter(url => url !== variantImage) || [];
      return dropGridCover([variantImage, ...otherImages]);
    }

    if (product.images && product.images.length > 0) {
      return dropGridCover(product.images.map(img => img.url));
    }

    return [product.thumbnail];
  };

  const images = getVariantImages();

  const handleVariantChange = (variant) => {
    setSelectedVariant(variant);
    setCurrentImageIndex(0);
  };

  const handleNextImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const handlePrevImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  const handleAddToCart = async () => {
    if (buttonState !== 'idle' || !selectedVariant) return;
    if (!isVariantInStock(selectedVariant)) return;

    setButtonState('loading');
    
    try {
      await addToCart(product, selectedVariant.id, 1);
      setButtonState('added');
      setTimeout(() => setButtonState('idle'), 2000);
    } catch (err) {
      console.error('Error adding to cart:', err);
      setButtonState('idle');
    }
  };

  // Medusa guarda los precios en unidades menores (centavos). Convertimos siempre
  // a unidad mayor dividiendo entre 100 — consistente con CartContext, ProductCard
  // y useProducts. NUNCA condicionar el divisor al valor del monto: rompe precios
  // chicos (un producto de $50 quedaría como $5,000).
  const getVariantPrice = (variant) => {
    if (!variant) return 0;
    if (variant.calculated_price?.calculated_amount) {
      return variant.calculated_price.calculated_amount;
    }
    if (variant.prices && variant.prices.length > 0) {
      const price = variant.prices.find(p => p.currency_code === 'mxn') || variant.prices[0];
      return price.amount;
    }
    return 0;
  };

  // Formatea el monto ya en unidad mayor para mostrar en UI.
  const formatPrice = (amount) => formatAmount(amount);

  // Contenido del botón con puntitos de loading
  const getButtonContent = () => {
    // Variante agotada: el botón sólo dice "Agotado", sin precio ni loader.
    if (selectedVariant && !isVariantInStock(selectedVariant)) {
      return 'Agotado';
    }
    // Distribuidora: el botón muestra su precio de socia (igual que el bloque de precio).
    // Con % de descuento activo, el precio público ya va rebajado.
    const publicPrice = rebajado ?? getVariantPrice(selectedVariant);
    const socioPrice = selectedVariant?.distributor_amount;
    const price = formatPrice(
      isDistributor && typeof socioPrice === 'number' && socioPrice < publicPrice ? socioPrice : publicPrice
    );
    switch (buttonState) {
      case 'loading':
        return <ButtonLoadingDots />;
      case 'added':
        return (
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            Añadido
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
        );
      default:
        return `Añadir a la cesta - $${price}`;
    }
  };

  // Clases del botón según estado. Si la variante seleccionada está agotada,
  // se añade .btn-out-of-stock para pintarlo en gris y bloquear el hover.
  const getButtonClasses = () => {
    const base = 'add-to-cart-btn';
    if (selectedVariant && !isVariantInStock(selectedVariant)) return `${base} btn-out-of-stock`;
    if (buttonState === 'loading') return `${base} btn-loading`;
    if (buttonState === 'added') return `${base} btn-added`;
    return base;
  };

  const getVariantOptionValue = (variant) => {
    if (variant.options && variant.options.length > 0) {
      return variant.options[0].value;
    }
    return variant.title || 'Default';
  };

  const getOptionTitle = () => {
    if (product.options && product.options.length > 0) {
      return product.options[0].title;
    }
    return 'Tamaño';
  };

  if (loading) {
    return (
      <div className="product-detail-phys product-detail-phys--loading" data-header-theme="light">
        <LoadingDots overlay={false} />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-detail-phys">
        <div className="error">
          <p>{error || 'Producto no encontrado'}</p>
          <Link to="/shop" className="back-link">Volver a la tienda</Link>
        </div>
      </div>
    );
  }

  // Ofertas del producto: un "compra y llévate" (2x1/3x2) y/o un %; con % se
  // muestra el precio rebajado.
  const ofertasProd = product ? (promo2x1.ofertas[product.id] || []) : [];
  const ofertaBuyGet = buyGetDe(ofertasProd);
  const ofertaDesc = descuentoDe(ofertasProd);
  const ofertaEnVariante = !!(selectedVariant && ofertasProd.length && promo2x1.variantIds.has(selectedVariant.id));
  const rebajado = ofertaEnVariante ? precioConDescuento(getVariantPrice(selectedVariant), ofertaDesc, selectedVariant.id) : null;
  const currentPrice = formatPrice(rebajado ?? getVariantPrice(selectedVariant));
  const simpleSpec = getVariantSpec(product.variants?.[0]);
  // Bloque dual "Precio público | Precio socio": sólo se renderiza para
  // clientas con sesión iniciada como distribuidora aprobada.
  // El fetch de productos no manda Authorization, así que calculated_amount
  // siempre llega como precio público; distributor_amount viene anexado por
  // attachDistributorPrices() y es el precio del price list "Distribuidoras".
  const distributorAmountRaw =
    typeof selectedVariant?.distributor_amount === 'number'
      ? selectedVariant.distributor_amount
      : null;
  // getVariantPrice ya devuelve unidad mayor (MXN), no requiere normalización extra.
  const publicAmountMajor = getVariantPrice(selectedVariant);
  const showDistributorPrice =
    isDistributor &&
    distributorAmountRaw != null &&
    distributorAmountRaw < publicAmountMajor;
  const category = product.collection?.title || product.categories?.[0]?.name || 'Productos';
  // toSitePath: quita http://localhost… de las fotos/videos (ver utils/sitePath).
  const metadata = sitePathsIn(product.metadata || {});
  const isKit = ['kit', 'set'].includes((product.type?.value || '').trim().toLowerCase());

  // Más detalles - desde metadata o vacío si no hay
  const moreDetails = metadata.more_details && metadata.more_details.length > 0 
    ? metadata.more_details.filter(d => d.title && d.title.trim() !== '')
    : [];

  // Determinar si usar video o imagen para cada sección
  const hasBenefitsVideo = metadata.texture_video && metadata.texture_video.trim() !== '';
  const hasDetailsVideo = metadata.details_video && metadata.details_video.trim() !== '';

  // ─── SEO ──────────────────────────────────────────────────────────────
  // Precio en unidades mayores (MXN). Si no hay precio real, productJsonLd
  // omite el bloque offers para no romper validación de Search Console.
  const seoPrice = publicAmountMajor && publicAmountMajor > 0 ? publicAmountMajor : null;
  const seoImage = (product.thumbnail || product.images?.[0]?.url) || undefined;
  const seoDescription = (product.description || `${product.title} — ${category}. Formulado por Physiology Skincare Co.`).slice(0, 300);
  const productLd = productJsonLd({
    title: product.title,
    description: seoDescription,
    image: images,
    sku: selectedVariant?.sku,
    handle: product.handle,
    price: seoPrice,
  });
  const breadcrumbLd = breadcrumbJsonLd([
    { name: 'Inicio', url: '/' },
    { name: category, url: '/shop' },
    { name: product.title, url: `/products/${product.handle || product.id}` },
  ]);

  return (
    <div className="product-detail-phys product-detail-phys--enter bg-[var(--phys-snow)] text-[var(--phys-ink)]" data-header-theme="light">
      <SEO
        title={product.title}
        description={seoDescription}
        path={`/products/${product.handle || product.id}`}
        image={seoImage}
        type="product"
        jsonLd={[productLd, breadcrumbLd]}
      />
      {/* ============================================
          HERO SECTION
          ============================================ */}
      <div className={`product-detail-wrapper${isKit ? ' is-kit' : ''}`}>
        {/* Breadcrumbs */}
        <nav className="breadcrumbs" aria-label="Migas de pan">
          <Link to="/">Inicio</Link>
          <span className="breadcrumbs-sep" aria-hidden="true">•</span>
          <Link to="/shop">{category}</Link>
          <span className="breadcrumbs-sep" aria-hidden="true">•</span>
          <span className="breadcrumbs-current" aria-current="page">{product.title}</span>
        </nav>

        {/* Image Gallery */}
        <div className="product-image-column">
          <div className="image-gallery-phys">
            {!isProductInStock(product) && (
              <span className="product-oos-badge" aria-label="Producto agotado">Agotado</span>
            )}
            <div
              className="gallery-main-image"
              onClick={() => setIsFullscreen(true)}
              onMouseEnter={() => setZoomCursor((c) => ({ ...c, visible: true }))}
              onMouseLeave={() => setZoomCursor((c) => ({ ...c, visible: false }))}
              onMouseMove={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setZoomCursor({
                  x: e.clientX - rect.left,
                  y: e.clientY - rect.top,
                  visible: true,
                });
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsFullscreen(true);
                }
              }}
              aria-label="Ampliar imagen a pantalla completa"
            >
              <img
                key={currentImageIndex}
                src={images[currentImageIndex]}
                alt={`${product.title} - ${currentImageIndex + 1}`}
              />
              <span
                className={`gallery-zoom-follower${zoomCursor.visible ? ' is-visible' : ''}`}
                style={{ left: zoomCursor.x, top: zoomCursor.y }}
                aria-hidden="true"
              >
                +
              </span>
            </div>

            {images.length > 1 && (
              <div className="gallery-controls">
                <button
                  className="gallery-arrow gallery-arrow-prev"
                  onClick={handlePrevImage}
                  aria-label="Imagen anterior"
                >
                  <svg width="14" height="14" viewBox="0 0 50 50">
                    <polygon points="25,31.3 4.2,10.5 0.1,14.6 25,39.5 50,14.6 45.9,10.5"/>
                  </svg>
                </button>

                <div className="gallery-counter">
                  {currentImageIndex + 1} / {images.length}
                </div>

                <button
                  className="gallery-arrow gallery-arrow-next"
                  onClick={handleNextImage}
                  aria-label="Siguiente imagen"
                >
                  <svg width="14" height="14" viewBox="0 0 50 50">
                    <polygon points="25,31.3 4.2,10.5 0.1,14.6 25,39.5 50,14.6 45.9,10.5"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Product Info */}
        <div className="product-info-column">
          <header className="product-header">
            <h1 className="product-name text-[var(--phys-ink)]">{product.title}</h1>
            {(metadata.subtitle || product.subtitle) && (
              <div className="product-badge">{metadata.subtitle || product.subtitle}</div>
            )}
          </header>

          <div className="product-description">
            <p>{product.description}</p>
          </div>

          {/* Variant Selector */}
          {product.variants && product.variants.length > 1 && (
            <div className="size-selector-section">
              <h2 className="section-title">{getOptionTitle()}</h2>
              <div className="size-options">
                {product.variants.map((variant) => (
                  <label key={variant.id} className="size-option">
                    <input
                      type="radio"
                      name="size"
                      checked={selectedVariant?.id === variant.id}
                      onChange={() => handleVariantChange(variant)}
                    />
                    <span className="size-radio"></span>
                    <span className="size-label">
                      {getVariantOptionValue(variant)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Tamaño / Porcentaje — solo productos simples (los de varias
              variantes ya lo muestran en el selector de arriba). */}
          {product.variants?.length === 1 && simpleSpec && (
            <p className="product-spec">
              {simpleSpec.label}: {simpleSpec.value}
            </p>
          )}

          {/* Price */}
          <div className="price-section">
            {showDistributorPrice ? (
              <div className="flex items-baseline gap-6 flex-wrap">
                <div className="flex flex-col items-start leading-tight">
                  <span className="text-[10px] tracking-[0.16em] uppercase text-[var(--phys-fog)]">Precio público</span>
                  <span className="price" style={{ color: 'var(--phys-slate)', fontWeight: 300 }}>${currentPrice}</span>
                </div>
                <div className="flex flex-col items-start leading-tight">
                  <span className="text-[10px] tracking-[0.16em] uppercase text-[var(--phys-steel)]">Precio socio</span>
                  <span className="price">${formatAmount(distributorAmountRaw)}</span>
                </div>
              </div>
            ) : (
              <span className="price">
                {rebajado != null && (
                  <span className="mr-2 font-light line-through text-[var(--phys-fog)]">
                    ${formatPrice(getVariantPrice(selectedVariant))}
                  </span>
                )}
                ${currentPrice}
              </span>
            )}
            {ofertaEnVariante && (
              <div className="mt-2 flex flex-col gap-1">
                {[ofertaBuyGet, ofertaDesc].filter(Boolean).map((o) => (
                  <p key={o.tipo} className="text-[12px] tracking-[0.08em] text-[var(--phys-steel)]">
                    <span className="inline-block px-2 py-0.5 mr-2 text-[10px] font-medium text-white bg-[var(--phys-steel)]">{etiquetaOferta(o)}</span>
                    {textoOferta(o)}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Add to Cart - Con puntitos de loading */}
          {(() => {
            const variantOutOfStock = selectedVariant && !isVariantInStock(selectedVariant);
            const btnColorClasses = variantOutOfStock
              ? 'bg-[var(--phys-frost)] text-[var(--phys-fog)] cursor-not-allowed'
              : 'bg-[#1a1a1a] text-[var(--phys-snow)]';
            return (
              <button
                className={`${getButtonClasses()} ${btnColorClasses}`}
                onClick={handleAddToCart}
                disabled={buttonState !== 'idle' || variantOutOfStock}
                aria-disabled={variantOutOfStock || undefined}
              >
                {getButtonContent()}
              </button>
            );
          })()}

          {/* Favorites */}
          <button
            className={`favorites-btn ${product && isInWishlist(product.id) ? 'active' : ''} text-[var(--phys-ink)] border-[var(--phys-frost)]`}
            onClick={() => product && toggleWishlist({
              id: product.id,
              title: product.title,
              name: product.title,
              thumbnail: product.thumbnail || product.images?.[0]?.url,
              image: product.thumbnail || product.images?.[0]?.url,
              price: selectedVariant?.calculated_price?.calculated_amount
                ? selectedVariant.calculated_price.calculated_amount
                : product.price || 0,
              handle: product.handle || product.id,
            })}
          >
            <svg width="16" height="15" viewBox="0 0 16 15" fill="none">
              <path d="M8 14.7038L7.18 13.9735C5.76667 12.6972 4.6 11.5952 3.68 10.6676C2.76 9.73998 2.02667 8.91104 1.48 8.18077C0.933333 7.45051 0.55 6.78933 0.33 6.19722C0.11 5.60512 0 5.00643 0 4.40117C0 3.21696 0.403333 2.22683 1.21 1.43077C2.01667 0.634721 3.01333 0.236694 4.2 0.236694C4.96 0.236694 5.66333 0.414326 6.31 0.769589C6.95667 1.12485 7.52 1.63801 8 2.30906C8.56 1.59854 9.15333 1.07551 9.78 0.739984C10.4067 0.404457 11.08 0.236694 11.8 0.236694C12.9867 0.236694 13.9833 0.634721 14.79 1.43077C15.5967 2.22683 16 3.21696 16 4.40117C16 5.00643 15.89 5.60512 15.67 6.19722C15.45 6.78933 15.0667 7.45051 14.52 8.18077C13.9733 8.91104 13.24 9.73998 12.32 10.6676C11.4 11.5952 10.2333 12.6972 8.82 13.9735L8 14.7038Z"
                fill={product && isInWishlist(product.id) ? '#000' : 'none'}
                stroke={product && isInWishlist(product.id) ? '#000' : '#666'}
                strokeWidth="1"
              />
            </svg>
            <span>{product && isInWishlist(product.id) ? 'Guardado en favoritos' : 'Guardar en favoritos'}</span>
          </button>

          {/* Sinergia Visible Section */}
          {synergiaProducts.length > 0 && (
            <div className="sinergia-section">
              <div className="sinergia-header">
                <span className="sinergia-label">Sinergia visible:</span>
              </div>
              
              {synergiaProducts.map((synProduct) => {
                const synPrice = formatPrice(getVariantPrice(synProduct.variants?.[0]));
                const isExpanded = expandedSynergia === synProduct.id;
                
                return (
                  <div key={synProduct.id} className="sinergia-item">
                    <div className="sinergia-item-main">
                      <div className="sinergia-item-info">
                        <h4 className="sinergia-item-name">{synProduct.title}</h4>
                        <span className="sinergia-item-price">${synPrice}</span>
                        <button 
                          className="sinergia-toggle"
                          onClick={() => setExpandedSynergia(isExpanded ? null : synProduct.id)}
                        >
                          Mostrar detalles
                          <svg 
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12"
                            className={isExpanded ? 'rotated' : ''}
                          >
                            <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                          </svg>
                        </button>
                      </div>
                      <Link to={`/products/${synProduct.handle || synProduct.id}`} className="sinergia-item-image">
                        <img 
                          src={synProduct.thumbnail} 
                          alt={synProduct.title}
                        />
                      </Link>
                    </div>
                    
                    {isExpanded && (
                      <div className="sinergia-item-details">
                        <p>{synProduct.description}</p>
                        {synProduct.subtitle && (
                          <p className="sinergia-item-subtitle">{synProduct.subtitle}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ============================================
          BENEFITS SECTION (Imagen/Video izq + Info der)
          ============================================ */}
      {(metadata.benefits_label || metadata.benefits_description) && (
        <div className="benefits-section">
          <div className="benefits-wrapper">
            {/* Media izquierda */}
            <div className="benefits-image">
              {hasBenefitsVideo ? (
                <VideoPlayer 
                  url={metadata.texture_video} 
                  className="benefits-video"
                />
              ) : (
                <img
                  src={metadata.texture_image || images[images.length - 1] || product.thumbnail}
                  alt={`${product.title} textura`}
                />
              )}
            </div>

            {/* Info derecha */}
            <div className="benefits-content">
              <span className="benefits-label">{metadata.benefits_label || 'Beneficios'}</span>
              
              <p className="benefits-description">
                {metadata.benefits_description || ''}
              </p>

              <div className="benefits-details">
                {/* Lista libre editable (usada en kits/sets: cada ítem es un
                    producto del kit con su nombre/tamaño y descripción). Se
                    edita en el admin. Si está vacía, no se muestra nada. */}
                {Array.isArray(metadata.benefits_items) &&
                  metadata.benefits_items
                    .filter((item) => item.title && item.title.trim() !== '')
                    .map((item, index) => (
                      <div key={`benefit-item-${index}`} className="benefit-item">
                        <h4>{item.title}</h4>
                        {item.content && <p>{item.content}</p>}
                      </div>
                    ))}

                {metadata.how_to_use && (
                  <div className="benefit-item">
                    <h4>Modo de uso</h4>
                    <p>{metadata.how_to_use}</p>
                  </div>
                )}

                {metadata.active_ingredients && (
                  <div className="benefit-item benefit-item-expandable">
                    <div className="benefit-item-header" onClick={() => setShowIngredientsPanel(!showIngredientsPanel)}>
                      <h4>Ingredientes activos</h4>
                      <button className="benefit-expand-btn">
                        <svg width="23" height="23" viewBox="0 0 50 50">
                          <circle cx="25" cy="25" r="22" fill="none" stroke="var(--phys-ink)" strokeWidth="3"/>
                          {showIngredientsPanel ? (
                            <rect x="15" y="23.9" width="20" height="2.5" fill="var(--phys-ink)" />
                          ) : (
                            <polygon
                              points="26.2,15.2 23.8,15.2 23.8,23.9 15,23.9 15,26.4 23.8,26.4 23.8,35.1 26.2,35.1 26.2,26.4 35,26.4 35,23.9 26.2,23.9"
                            />
                          )}
                        </svg>
                      </button>
                    </div>
                    <p>{metadata.active_ingredients}</p>
                  </div>
                )}

                {metadata.dosis && (
                  <div className="benefit-item">
                    <h4>Dosis</h4>
                    <p>{metadata.dosis}</p>
                  </div>
                )}

                {metadata.aroma && (
                  <div className="benefit-item">
                    <h4>Aroma</h4>
                    <p>{metadata.aroma}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================
          MORE DETAILS SECTION (Acordeones izq + Imagen/Video der)
          ============================================ */}
      {moreDetails.length > 0 && (
        <div className="more-details-section">
          <div className="more-details-wrapper">
            {/* Acordeones izquierda */}
            <div className="more-details-content">
              <span className="more-details-label">{metadata.details_label || 'Detalles'}</span>
              {metadata.details_sublabel && (
                <span className="more-details-sublabel">{metadata.details_sublabel}</span>
              )}

              <div className="accordion-list">
                {moreDetails.map((detail, index) => {
                  const isExpanded = expandedDetail === index;
                  return (
                    <div key={index} className="accordion-item">
                      <button 
                        className="accordion-header"
                        onClick={() => setExpandedDetail(isExpanded ? null : index)}
                      >
                        <span>{detail.title}</span>
                        <svg 
                          width="14" 
                          height="14" 
                          viewBox="0 0 14 14"
                          className={isExpanded ? 'rotated' : ''}
                        >
                          <path d="M2 5L7 10L12 5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                        </svg>
                      </button>
                      <div className={`accordion-content ${isExpanded ? 'open' : ''}`}>
                        <p>{detail.content}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Media derecha */}
            <div className="more-details-image">
              {hasDetailsVideo ? (
                <VideoPlayer 
                  url={metadata.details_video} 
                  className="details-video"
                />
              ) : (
                <img
                  src={metadata.details_image || metadata.texture_image || images[0] || product.thumbnail}
                  alt={`${product.title} detalle`}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============================================
          RESEÑAS
          ============================================ */}
      <ProductReviews product={product} />

      {/* ============================================
          RECOMMENDATIONS CAROUSEL
          ============================================ */}
      {recommendedProducts.length > 0 && (
        <ProductCarousel
          products={recommendedProducts}
          title="Otras recomendaciones"
          subtitle="Los imprescindibles para una rutina de skincare completa"
          linkText="Explorar tienda"
          linkUrl="/shop"
          minimal
        />
      )}

      {/* Ingredients Panel (Flyout) — portal a document.body para que
          position:fixed no quede contenido por el transform de
          .product-detail-phys--enter (que rompe el viewport anchor). */}
      {createPortal(
        <div
          className={`ingredients-panel-overlay ${showIngredientsPanel ? 'is-open' : ''}`}
          onClick={() => setShowIngredientsPanel(false)}
        >
          <div className="ingredients-panel" onClick={(e) => e.stopPropagation()}>
            <button
              className="panel-close-btn"
              onClick={() => setShowIngredientsPanel(false)}
            >
              ×
            </button>
            <h2>Ingredientes activos</h2>
            <div className="ingredients-content">
              <p>{metadata.active_ingredients || 'Sin información'}</p>
              {metadata.ingredients_detail && (
                <div className="ingredients-detail">
                  <h3>Lista completa (INCI)</h3>
                  <p>{metadata.ingredients_detail}</p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {isFullscreen && createPortal(
        <div
          className="gallery-fullscreen"
          role="dialog"
          aria-modal="true"
          aria-label="Galería ampliada"
          onMouseEnter={() => setFullscreenHover(true)}
          onMouseLeave={() => setFullscreenHover(false)}
        >
          <button
            className="gallery-fullscreen-close"
            onClick={() => setIsFullscreen(false)}
            aria-label="Cerrar galería"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <line x1="4" y1="4" x2="20" y2="20" />
              <line x1="20" y1="4" x2="4" y2="20" />
            </svg>
          </button>

          <img
            key={currentImageIndex}
            className="gallery-fullscreen-image"
            src={images[currentImageIndex]}
            alt={`${product.title} - ${currentImageIndex + 1}`}
          />

          {images.length > 1 && (
            <>
              <button
                className={`gallery-fullscreen-arrow gallery-fullscreen-arrow-prev ${fullscreenHover ? 'visible' : ''}`}
                onClick={handlePrevImage}
                aria-label="Imagen anterior"
              >
                <svg width="16" height="16" viewBox="0 0 50 50">
                  <polygon points="25,31.3 4.2,10.5 0.1,14.6 25,39.5 50,14.6 45.9,10.5" transform="rotate(90 25 25)" />
                </svg>
              </button>
              <button
                className={`gallery-fullscreen-arrow gallery-fullscreen-arrow-next ${fullscreenHover ? 'visible' : ''}`}
                onClick={handleNextImage}
                aria-label="Siguiente imagen"
              >
                <svg width="16" height="16" viewBox="0 0 50 50">
                  <polygon points="25,31.3 4.2,10.5 0.1,14.6 25,39.5 50,14.6 45.9,10.5" transform="rotate(-90 25 25)" />
                </svg>
              </button>
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}